import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "../lib/apiClient";
import { PageHeader, Card, Loading, PrimaryBtn, Field, Input, Select } from "../components/kit";

const LEVELS = ["Recreational", "Local junior tournaments", "Regional tournaments", "Provincial/state", "National"];
const EQUIP = ["No equipment", "Bands", "Dumbbells", "Medicine ball", "Full gym"];

export default function Profile() {
  const [p, setP] = useState(null);
  const [t, setT] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/profile").then((r) => setP(r.data || {}));
    api.get("/tournament").then((r) => setT(r.data || { name: "", start_date: "", tournament_date: "", course_par: 72, course_yardage: 6200 }));
  }, []);

  if (!p || !t) return <div className="p-6"><Loading /></div>;

  const saveProfile = async () => {
    setBusy(true);
    try {
      await api.post("/profile", {
        player_name: p.player_name, age: Number(p.age), gender: p.gender || null,
        handicap: Number(p.handicap), avg_score: p.avg_score ? Number(p.avg_score) : null,
        lowest_score: p.lowest_score ? Number(p.lowest_score) : null,
        dominant_hand: p.dominant_hand || "Right", competitive_level: p.competitive_level,
        facilities: p.facilities || [], equipment: p.equipment,
        strengths: p.strengths || [], strength_equipment: p.strength_equipment || "No equipment",
        minutes_per_day: Number(p.minutes_per_day) || 90, practice_days: p.practice_days || [],
        physical_limitations: p.physical_limitations || "", diagnostics: p.diagnostics || {},
      });
      toast.success("Profile saved");
    } catch { toast.error("Save failed"); } finally { setBusy(false); }
  };

  const saveTournament = async () => {
    if (!t.name || !t.start_date || !t.tournament_date) { toast.error("Fill tournament name & dates"); return; }
    setBusy(true);
    try {
      await api.post("/tournament", { name: t.name, start_date: t.start_date, tournament_date: t.tournament_date, course_par: Number(t.course_par) || 72, course_yardage: Number(t.course_yardage) || null });
      toast.loading("Rebuilding your plan…", { id: "t" });
      await api.post("/plan/generate");
      toast.success("Tournament saved & plan rebuilt", { id: "t" });
    } catch { toast.error("Save failed", { id: "t" }); } finally { setBusy(false); }
  };

  const set = (k, v) => setP((s) => ({ ...s, [k]: v }));

  return (
    <div className="p-5 sm:p-8 max-w-3xl mx-auto">
      <PageHeader eyebrow="Setup" title="Profile" />

      <Card className="p-6 mb-6 space-y-5">
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#CCFF00]">Player</div>
        <Field label="Player name"><Input data-testid="pf-name" value={p.player_name || ""} onChange={(e) => set("player_name", e.target.value)} /></Field>
        <div className="grid grid-cols-3 gap-4">
          <Field label="Age"><Input type="number" value={p.age ?? ""} onChange={(e) => set("age", e.target.value)} /></Field>
          <Field label="Handicap"><Input data-testid="pf-handicap" type="number" step="0.1" value={p.handicap ?? ""} onChange={(e) => set("handicap", e.target.value)} /></Field>
          <Field label="Minutes/day"><Input type="number" value={p.minutes_per_day ?? ""} onChange={(e) => set("minutes_per_day", e.target.value)} /></Field>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Avg score"><Input type="number" value={p.avg_score ?? ""} onChange={(e) => set("avg_score", e.target.value)} /></Field>
          <Field label="Lowest score"><Input type="number" value={p.lowest_score ?? ""} onChange={(e) => set("lowest_score", e.target.value)} /></Field>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Competitive level"><Select value={p.competitive_level || LEVELS[1]} onChange={(e) => set("competitive_level", e.target.value)}>{LEVELS.map((l) => <option key={l}>{l}</option>)}</Select></Field>
          <Field label="Strength equipment"><Select value={p.strength_equipment || EQUIP[0]} onChange={(e) => set("strength_equipment", e.target.value)}>{EQUIP.map((x) => <option key={x}>{x}</option>)}</Select></Field>
        </div>
        <Field label="Physical limitations (optional)"><Input value={p.physical_limitations || ""} onChange={(e) => set("physical_limitations", e.target.value)} /></Field>
        <PrimaryBtn data-testid="pf-save" onClick={saveProfile} disabled={busy}>Save Profile</PrimaryBtn>
      </Card>

      <Card className="p-6 space-y-5">
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#CCFF00]">Tournament</div>
        <Field label="Tournament name"><Input data-testid="pf-tname" value={t.name || ""} onChange={(e) => setT({ ...t, name: e.target.value })} /></Field>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Training start"><Input data-testid="pf-start" type="date" value={t.start_date || ""} onChange={(e) => setT({ ...t, start_date: e.target.value })} /></Field>
          <Field label="Tournament date"><Input data-testid="pf-tdate" type="date" value={t.tournament_date || ""} onChange={(e) => setT({ ...t, tournament_date: e.target.value })} /></Field>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Course par"><Input type="number" value={t.course_par ?? 72} onChange={(e) => setT({ ...t, course_par: e.target.value })} /></Field>
          <Field label="Course yardage"><Input type="number" value={t.course_yardage ?? ""} onChange={(e) => setT({ ...t, course_yardage: e.target.value })} /></Field>
        </div>
        <PrimaryBtn data-testid="pf-tsave" onClick={saveTournament} disabled={busy}>Save Tournament & Rebuild Plan</PrimaryBtn>
      </Card>
    </div>
  );
}
